import React, { memo } from 'react';
import { Trash2, Undo2 } from 'lucide-react';
import { Input } from '../../ui';
import { getCurrencySymbol } from '../../../utils/currency';
import { BomSheetField, SheetRow } from './types';

/**
 * The sheet's cells, each memoized on its own primitive props. A keystroke
 * changes one cell's `value` (and its row's label), so only that cell — and,
 * when the name changes, its row — re-renders; a 300-line BOM stays quick.
 * Callbacks passed in must be stable (`useBomSheetDraft`'s are).
 */

export const FIELD_LABEL: Record<BomSheetField, string> = {
  name: 'Name',
  partNumber: 'Part number',
  mpn: 'MPN',
  manufacturer: 'Manufacturer',
  description: 'Description',
  quantity: 'Qty',
  targetPrice: 'Target price',
};

const MONO: Partial<Record<BomSheetField, boolean>> = { quantity: true, targetPrice: true };

const formatPrice = (raw: string, currency?: string) => {
  const n = Number(raw);
  if (raw === '' || !Number.isFinite(n) || n <= 0) return null;
  const symbol = currency ? getCurrencySymbol(currency) : '';
  return `${symbol}${n.toLocaleString('en-IN', {
    minimumFractionDigits: Number.isInteger(n) ? 0 : 2,
    maximumFractionDigits: 2,
  })}`;
};

interface EditableCellProps {
  row: SheetRow;
  field: BomSheetField;
  value: string;
  dirty: boolean;
  error?: string;
  disabled: boolean;
  /** The row's name, for the accessible label. */
  lineName: string;
  onChange: (row: SheetRow, field: BomSheetField, value: string) => void;
}

export const EditableCell = memo<EditableCellProps>(
  ({ row, field, value, dirty, error, disabled, lineName, onChange }) => (
    <Input
      cell
      value={value}
      dirty={dirty}
      error={error}
      disabled={disabled}
      onChange={(e) => onChange(row, field, e.target.value)}
      inputMode={field === 'quantity' ? 'numeric' : field === 'targetPrice' ? 'decimal' : undefined}
      aria-label={`${FIELD_LABEL[field]}, ${lineName}`}
      className={`${MONO[field] ? 'text-right font-mono' : ''} ${field === 'name' ? 'font-semibold' : ''}`}
    />
  )
);
EditableCell.displayName = 'EditableCell';

interface ReadCellProps {
  field: BomSheetField;
  value: string;
  removed: boolean;
  currency?: string;
}

export const ReadCell = memo<ReadCellProps>(({ field, value, removed, currency }) => {
  const shown = field === 'targetPrice' ? formatPrice(value, currency) : value;
  return (
    <span
      className={`truncate ${MONO[field] ? 'font-mono' : ''} ${field === 'name' ? 'font-semibold text-ink' : ''}
        ${removed ? 'text-ink-muted line-through' : ''}`}
      title={value || undefined}
    >
      {shown || <span className="text-ink-muted">—</span>}
    </span>
  );
});
ReadCell.displayName = 'ReadCell';

interface NumberCellProps {
  number: number;
  blamed: boolean;
  /** "Missing quantity, part number or MPN" — empty when the line is complete. */
  missing: string;
}

export const NumberCell = memo<NumberCellProps>(({ number, blamed, missing }) => (
  <span className="flex items-center gap-[3px]">
    <span className={blamed ? 'font-semibold text-danger-ink' : 'text-ink-muted'}>
      {String(number).padStart(2, '0')}
    </span>
    {missing && (
      <span
        className="h-[5px] w-[5px] shrink-0 rounded-round bg-warn-ink"
        title={missing}
        aria-label={missing}
        role="img"
      />
    )}
  </span>
));
NumberCell.displayName = 'NumberCell';

interface RemoveCellProps {
  row: SheetRow;
  removed: boolean;
  disabled: boolean;
  lineName: string;
  onToggle: (row: SheetRow) => void;
}

export const RemoveCell = memo<RemoveCellProps>(({ row, removed, disabled, lineName, onToggle }) => (
  <button
    type="button"
    onClick={() => onToggle(row)}
    disabled={disabled}
    aria-label={removed ? `Keep ${lineName}` : `Remove ${lineName}`}
    title={removed ? 'Keep this line' : 'Remove this line'}
    className="inline-flex h-6 w-6 items-center justify-center rounded-control text-ink-muted transition-colors
      hover:bg-neutral-bg hover:text-ink focus:outline-none focus-visible:ring-2 focus-visible:ring-action
      disabled:opacity-50"
  >
    {removed ? <Undo2 className="h-[13px] w-[13px]" /> : <Trash2 className="h-[13px] w-[13px]" />}
  </button>
));
RemoveCell.displayName = 'RemoveCell';
